import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Filter,
  MoreVertical,
  DollarSign,
  Calendar,
  User,
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  Eye,
  Trash2,
  Edit2,
  X,
  ChevronDown,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const PaymentStatusBadge = ({ status }) => {
  const styles = {
    Completed: "bg-green-500/20 text-green-400 border-green-500/50",
    Pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
    Failed: "bg-red-500/20 text-red-400 border-red-500/50",
  };

  const icons = {
    Completed: <CheckCircle2 className="w-3 h-3" />,
    Pending: <Clock className="w-3 h-3" />,
    Failed: <XCircle className="w-3 h-3" />,
  };

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${
        styles[status] || styles.Pending
      }`}
    >
      {icons[status]}
      {status}
    </div>
  );
};

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // const [editingPayment, setEditingPayment] = useState(null); // Editing not supported by API yet
  // const [viewingPayment, setViewingPayment] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    studentId: "",
    courseId: "",
    amount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentStatus: "Completed",
    paymentMethod: "UPI",
    transactionId: "",
  });

  useEffect(() => {
    fetchConfigData();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [currentPage]); // Add dependencies if search/filter supports backend params

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/payment/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: {
          pageNo: currentPage,
          pageSize: pageSize,
        },
      });

      if (data.success) {
        setPayments(data.data || []);
        setTotalRecords(data.count || 0);
      } else {
        toast.error(data.message || "Failed to fetch payments");
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Error loading payments");
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigData = async () => {
    try {
      setLoadingConfig(true);
      const [studentsRes, coursesRes] = await Promise.all([
        axios.get(`${server}/student/all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: { pageNo: 1, pageSize: 1000 },
        }),
        axios.get(`${server}/course/all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (studentsRes.data.success) {
        setStudents(studentsRes.data.data || []);
      }
      if (coursesRes.data.success) {
        setCourses(coursesRes.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load students and courses");
    } finally {
      setLoadingConfig(false);
    }
  };

  // Derived State (Client-side filtering for now as API search params unknown)
  const filteredPayments = payments.filter((payment) => {
    const studentName = payment.studentName || "";
    const courseName = payment.courseName || "";
    const tid = payment.transactionId || "";

    const matchesSearch =
      studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tid.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      filterStatus === "All" || payment.paymentStatus === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const totalCollected = payments
    .filter((p) => p.paymentStatus === "Completed")
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const pendingAmount = payments
    .filter((p) => p.paymentStatus === "Pending")
    .reduce((sum, p) => sum + Number(p.amount), 0);

  // Handlers
  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const payload = {
        studentId: Number(formData.studentId),
        courseId: Number(formData.courseId),
        amount: Number(formData.amount),
        paymentDate: formData.paymentDate, // Ensure format is correct, API exptected format likely ISO string or YYYY-MM-DD
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentStatus,
        transactionId: formData.transactionId,
      };

      const { data } = await axios.post(`${server}/payment/new`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (data.success) {
        toast.success("Payment recorded successfully");
        fetchPayments(); // Refresh list
        handleCloseModal();
      } else {
        toast.error(data.message || "Failed to record payment");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Error saving payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    // API for delete not provided yet
    toast.info("Delete functionality coming soon (API pending)");
  };

  const openEditModal = (payment) => {
    // API for update not provided yet
    toast.info("Edit functionality coming soon (API pending)");
    // setEditingPayment(payment);
    // setFormData({ ...payment }); // Map fields if enabled
    // setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // setEditingPayment(null);
    setFormData({
      studentId: "",
      courseId: "",
      amount: "",
      paymentDate: new Date().toISOString().split("T")[0],
      paymentStatus: "Completed",
      paymentMethod: "UPI",
      transactionId: "",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Payments</h1>
            <p className="text-gray-400">
              Manage student fees and transactions
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-500 transition-colors shadow-lg shadow-green-900/20"
          >
            <Plus className="w-5 h-5" />
            Record Payment
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-gray-800/50 backdrop-blur-xl border border-gray-700/50 rounded-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500/10 rounded-xl">
                <Wallet className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-xs font-medium px-2 py-1 bg-green-500/10 text-green-400 rounded-lg">
                +12.5%
              </span>
            </div>
            <p className="text-gray-400 text-sm">Total Collected</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              ₹{totalCollected.toLocaleString()}
            </h3>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 bg-gray-800/50 backdrop-blur-xl border border-gray-700/50 rounded-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-500/10 rounded-xl">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <span className="text-xs font-medium px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded-lg">
                Pending
              </span>
            </div>
            <p className="text-gray-400 text-sm">Pending Payments</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              ₹{pendingAmount.toLocaleString()}
            </h3>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-gray-800/50 backdrop-blur-xl border border-gray-700/50 rounded-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/10 rounded-xl">
                <ArrowUpRight className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className="text-gray-400 text-sm">Today's Transactions</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              {
                payments.filter(
                  (p) => p.date === new Date().toISOString().split("T")[0]
                ).length
              }
            </h3>
          </motion.div>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 bg-gray-800/30 p-4 rounded-2xl border border-gray-700/30 backdrop-blur-sm">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student, course, or transaction ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900/50 border border-gray-700 rounded-xl py-2.5 pl-10 pr-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-green-500/50 transition-colors"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {["All", "Completed", "Pending", "Failed"].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                  filterStatus === status
                    ? "bg-gray-700 text-white"
                    : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-gray-300"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Payments List */}
        <div className="grid gap-4">
          <AnimatePresence mode="popLayout">
            {filteredPayments.map((payment, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                key={payment.id}
                className="group p-4 bg-gray-800/40 border border-gray-700/50 hover:border-gray-600/50 rounded-2xl transition-all hover:bg-gray-800/60"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-700/30 rounded-xl hidden md:block">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        {payment.studentName}
                        <PaymentStatusBadge status={payment.paymentStatus} />
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {payment.courseName}
                        </span>
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-3.5 h-3.5" />
                          {payment.paymentMethod} • {payment.transactionId}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(payment.paymentDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:justify-end gap-6 w-full md:w-auto mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-700/50">
                    <div className="text-right">
                      <span className="text-sm text-gray-400 block md:hidden">
                        Amount
                      </span>
                      <span className="text-xl font-bold text-white">
                        ₹{Number(payment.amount).toLocaleString()}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(payment)}
                        className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(payment.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredPayments.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex p-4 bg-gray-800/50 rounded-full mb-4">
                <Search className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-1">
                No payments found
              </h3>
              <p className="text-gray-400">
                Try adjusting your search or filters
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
            >
              <div className="p-6 border-b border-gray-800 flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">
                  Record New Payment
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Student Name
                    </label>
                    <div className="relative">
                      {loadingConfig && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {/* Spinner could go here, but omitted for simplicity */}
                        </div>
                      )}
                      <select
                        required
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors appearance-none"
                        value={formData.studentId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            studentId: e.target.value,
                          })
                        }
                        disabled={loadingConfig}
                      >
                        <option value="">Select Student</option>
                        {students.map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.firstName} {student.lastName} (
                            {student.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Course Name
                    </label>
                    <select
                      required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors appearance-none"
                      value={formData.courseId}
                      onChange={(e) =>
                        setFormData({ ...formData, courseId: e.target.value })
                      }
                      disabled={loadingConfig}
                    >
                      <option value="">Select Course</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.courseName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        ₹
                      </span>
                      <input
                        required
                        type="number"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({ ...formData, amount: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Date
                    </label>
                    <input
                      required
                      type="date"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors"
                      value={formData.paymentDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentDate: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Payment Method
                    </label>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors appearance-none"
                      value={formData.paymentMethod}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentMethod: e.target.value,
                        })
                      }
                    >
                      <option value="UPI">UPI</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Status
                    </label>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors appearance-none"
                      value={formData.paymentStatus}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentStatus: e.target.value,
                        })
                      }
                    >
                      <option value="Completed">Completed</option>
                      <option value="Pending">Pending</option>
                      <option value="Failed">Failed</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Transaction ID / Remarks
                    </label>
                    <input
                      type="text"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-green-500 focus:outline-none transition-colors"
                      value={formData.transactionId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          transactionId: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-800">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-500 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    {submitting && (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )}
                    Save Payment
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentsPage;
